import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, FloppyDisk, PaperPlaneRight, Sparkle } from "@phosphor-icons/react";
import ReactMarkdown from "react-markdown";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";

/**
 * Two-stage gate for the 6 Months Mentorship tab:
 *   1. Accept Terms & Conditions (full-page read-and-accept)
 *   2. Fill the 26-question Orientation Form (5 sections, mandatory submit)
 *
 * Both are independent endpoints on the backend; the frontend orchestrates
 * the flow and once both are complete, the user can open any Mentorship GPT
 * they've been granted access to.
 */

// Field rendering is now driven entirely by the backend metadata returned
// from /api/onboarding/orientation. Each field carries: type, label,
// section, and (depending on type) placeholder/description/options/scale.
// No client-side overrides needed.

export default function MentorshipOnboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialStep = params.get("step") === "orientation" ? "orientation" : "tnc";

  const [step, setStep] = useState(initialStep);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [tncText, setTncText] = useState("");
  const [tncStatus, setTncStatus] = useState(null);
  const [form, setForm] = useState({});
  const [fields, setFields] = useState([]); // [{key, label, section, type, options, scale, description, placeholder}]
  const [sectionDescriptions, setSectionDescriptions] = useState({});
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [tncRes, oriRes] = await Promise.all([
          api.get("/onboarding/tnc"),
          api.get("/onboarding/orientation"),
        ]);
        setTncText(tncRes.data.text);
        setTncStatus(tncRes.data);
        setFields(oriRes.data.fields);
        setSectionDescriptions(oriRes.data.section_descriptions || {});
        setForm(oriRes.data.form || {});
        // If T&C is already accepted (or the user is admin / FULL_ACCESS
        // bypass), default to the orientation step so the form is what the
        // user sees first. We intentionally do NOT auto-redirect away from
        // this page when mentorship_unlocked is true — the previous behavior
        // bounced FULL_ACCESS users back to the dashboard which made the
        // form appear "not to open" when they followed the CTA.
        if (tncRes.data.tnc_accepted && initialStep !== "orientation") {
          setStep("orientation");
        }
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load onboarding");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refreshUser = (statusPayload) => {
    if (user && typeof user === "object") {
      setUser({
        ...user,
        mentorship_tnc_accepted: statusPayload.tnc_accepted,
        mentorship_orientation_submitted: statusPayload.orientation_submitted,
      });
    }
  };

  const acceptTnc = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/onboarding/tnc/accept");
      setTncStatus({ ...tncStatus, ...data });
      refreshUser(data);
      setStep("orientation");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not record acceptance");
    } finally {
      setSubmitting(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSavedAt(null);
  };

  const saveDraft = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put("/onboarding/orientation", form);
      setForm(data.form);
      setSavedAt(new Date());
      refreshUser(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const submitOrientation = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.put("/onboarding/orientation?submit=true", form);
      setForm(data.form);
      refreshUser(data);
      navigate("/?onboarding=done", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background" data-testid="onboarding-loading">
        <Navbar />
        <div className="py-20 flex justify-center"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="mentorship-onboarding-page">
      <Navbar />
      <main className="onboarding-shell">
        <button onClick={() => navigate("/")} className="kb-back" data-testid="onboarding-back">
          <ArrowLeft size={14} weight="bold" /> Back to Dashboard
        </button>

        <ol className="onboarding-progress" aria-label="Progress">
          <li className={`onboarding-step ${step === "tnc" ? "is-current" : tncStatus?.tnc_accepted ? "is-done" : ""}`} data-testid="step-tnc">
            <span className="onboarding-step-num">{tncStatus?.tnc_accepted ? "✓" : "1"}</span> Terms & Conditions
          </li>
          <li className={`onboarding-step ${step === "orientation" ? "is-current" : tncStatus?.orientation_submitted ? "is-done" : ""}`} data-testid="step-orientation">
            <span className="onboarding-step-num">{tncStatus?.orientation_submitted ? "✓" : "2"}</span> Orientation form
          </li>
        </ol>

        {error && <p className="error-banner" data-testid="onboarding-error">{error}</p>}

        {step === "tnc" && (
          <TermsStep
            text={tncText}
            already={!!tncStatus?.tnc_accepted}
            onAccept={acceptTnc}
            submitting={submitting}
          />
        )}

        {step === "orientation" && (
          <OrientationStep
            fields={fields}
            sectionDescriptions={sectionDescriptions}
            form={form}
            updateField={updateField}
            saveDraft={saveDraft}
            saving={saving}
            savedAt={savedAt}
            submitOrientation={submitOrientation}
            submitting={submitting}
          />
        )}
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function TermsStep({ text, already, onAccept, submitting }) {
  const [agreed, setAgreed] = useState(already);

  return (
    <section className="onboarding-card" data-testid="tnc-step">
      <header className="onboarding-head">
        <div className="kb-eyebrow-wrap">
          <span className="kb-eyebrow-icon"><Sparkle size={16} weight="duotone" /></span>
          <p className="label-uppercase kb-eyebrow">Step 1 of 2</p>
        </div>
        <h1 className="kb-title">Welcome to the <em>6&nbsp;Months Mentorship</em>.</h1>
        <p className="kb-sub">
          Read these terms once before you go further. You only need to do this once — Akshat keeps it short.
        </p>
      </header>

      <article className="tnc-scroll markdown-body" data-testid="tnc-text">
        <ReactMarkdown>{text}</ReactMarkdown>
      </article>

      <label className="onboarding-checkbox" data-testid="tnc-checkbox-label">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          data-testid="tnc-checkbox"
        />
        <span>I have read and agree to these Terms & Conditions.</span>
      </label>

      <div className="onboarding-actions">
        <button
          className="btn btn-accent"
          onClick={onAccept}
          disabled={!agreed || submitting}
          data-testid="tnc-accept"
        >
          {submitting ? "Recording…" : already ? "Continue to orientation →" : "Accept & continue"}
        </button>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

function OrientationStep({ fields, sectionDescriptions = {}, form, updateField, saveDraft, saving, savedAt, submitOrientation, submitting }) {
  // Group fields by section, preserving the order the backend returned.
  const sections = [];
  for (const f of fields) {
    let last = sections[sections.length - 1];
    if (!last || last.section !== f.section) {
      last = { section: f.section, items: [] };
      sections.push(last);
    }
    last.items.push(f);
  }

  const totalFilled = fields.filter((f) => {
    const v = form[f.key];
    if (v === null || v === undefined) return false;
    if (typeof v === "string") return v.trim().length > 0;
    return true;
  }).length;

  const allDone = totalFilled === fields.length;

  return (
    <section className="onboarding-card" data-testid="orientation-step">
      <header className="onboarding-head">
        <div className="kb-eyebrow-wrap">
          <span className="kb-eyebrow-icon"><Sparkle size={16} weight="duotone" /></span>
          <p className="label-uppercase kb-eyebrow">Step 2 of 2 · High-Value Consulting Orientation</p>
        </div>
        <h1 className="kb-title">A few sharp questions, <em>then we begin</em>.</h1>
        <p className="kb-sub">
          {fields.length} questions across {sections.length} sections. Be honest — your coach reads this before
          the first session. Save anytime; you can come back. Once every field is filled, hit Submit to unlock
          the mentorship GPTs.
        </p>
      </header>

      <div className="orientation-sticky-bar" data-testid="orientation-sticky-progress">
        <span className="orientation-sticky-meta">Progress</span>
        <div className="orientation-sticky-progress">
          <div
            className="orientation-sticky-fill"
            style={{ width: `${(totalFilled / Math.max(fields.length, 1)) * 100}%` }}
          />
        </div>
        <span className="orientation-sticky-count" data-testid="orientation-progress">
          {totalFilled} / {fields.length}
        </span>
      </div>

      <div className="orientation-sections">
        {sections.map((sec, sIdx) => (
          <fieldset key={sec.section} className="orientation-section" data-testid={`section-${sIdx}`}>
            <legend className="orientation-legend">
              <span className="orientation-legend-num">{String(sIdx + 1).padStart(2, "0")}</span>
              {sec.section}
            </legend>
            {sectionDescriptions[sec.section] && (
              <p className="orientation-section-desc" data-testid={`section-desc-${sIdx}`}>
                {sectionDescriptions[sec.section]}
              </p>
            )}
            {sec.items.map((f) => (
              <OrientationField
                key={f.key}
                field={f}
                value={form[f.key]}
                onChange={(v) => updateField(f.key, v)}
              />
            ))}
          </fieldset>
        ))}
      </div>

      <div className="onboarding-actions">
        {savedAt && (
          <span className="kb-saved" data-testid="orientation-saved">
            <CheckCircle size={14} weight="fill" /> Draft saved at {savedAt.toLocaleTimeString()}
          </span>
        )}
        <button
          className="btn btn-ghost"
          onClick={saveDraft}
          disabled={saving}
          data-testid="orientation-save-draft"
        >
          <FloppyDisk size={14} /> {saving ? "Saving…" : "Save draft"}
        </button>
        <button
          className="btn btn-accent"
          onClick={submitOrientation}
          disabled={!allDone || submitting}
          title={!allDone ? "Complete all questions to submit" : "Submit and unlock the mentorship GPTs"}
          data-testid="orientation-submit"
        >
          <PaperPlaneRight size={14} weight="bold" />
          {submitting ? "Submitting…" : "Submit & unlock mentorship"}
        </button>
      </div>
    </section>
  );
}

function OrientationField({ field, value, onChange }) {
  const { key, label, type, placeholder, description, options, scale } = field;
  const hasValue =
    value !== null && value !== undefined && (typeof value !== "string" || value.trim().length > 0);

  // Linear scale (1-5) — used for Section 3 self-assessment ratings.
  if (type === "linear_scale") {
    const min = scale?.min ?? 1;
    const max = scale?.max ?? 5;
    const minLabel = scale?.min_label || "Low";
    const maxLabel = scale?.max_label || "High";
    const ticks = [];
    for (let n = min; n <= max; n++) ticks.push(n);
    return (
      <div className="orientation-field" data-testid={`orientation-field-${key}`}>
        <label className="orientation-label">
          {label}{!hasValue && <span className="orientation-required"> *</span>}
        </label>
        <div className="orientation-scale-row">
          <span className="orientation-scale-label-min" aria-hidden="true">{minLabel}</span>
          <div className="rating-pills" role="radiogroup" aria-label={label}>
            {ticks.map((n) => (
              <button
                key={n}
                type="button"
                className={`rating-pill ${value === n ? "is-active" : ""}`}
                onClick={() => onChange(n)}
                aria-label={`${n} — ${n === min ? minLabel : n === max ? maxLabel : "rating " + n}`}
                aria-pressed={value === n}
                data-testid={`orientation-rating-${key}-${n}`}
              >
                {n}
              </button>
            ))}
          </div>
          <span className="orientation-scale-label-max" aria-hidden="true">{maxLabel}</span>
        </div>
      </div>
    );
  }

  // Multiple choice — radio group with one option per line.
  if (type === "multiple_choice") {
    const opts = Array.isArray(options) ? options : [];
    return (
      <div className="orientation-field" data-testid={`orientation-field-${key}`}>
        <label className="orientation-label">
          {label}{!hasValue && <span className="orientation-required"> *</span>}
        </label>
        {description && <p className="orientation-help">{description}</p>}
        <div className="orientation-choice-list" role="radiogroup" aria-label={label}>
          {opts.map((opt) => {
            const selected = value === opt;
            return (
              <label
                key={opt}
                className={`orientation-choice ${selected ? "is-selected" : ""}`}
                data-testid={`orientation-choice-${key}-${opt.replace(/\W+/g, "_").slice(0, 30)}`}
              >
                <input
                  type="radio"
                  name={`o-${key}`}
                  value={opt}
                  checked={selected}
                  onChange={() => onChange(opt)}
                  className="orientation-choice-input"
                />
                <span className="orientation-choice-dot" aria-hidden="true" />
                <span className="orientation-choice-label">{opt}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  // Paragraph — multi-line textarea with optional helper description.
  if (type === "paragraph") {
    return (
      <div className="orientation-field" data-testid={`orientation-field-${key}`}>
        <label className="orientation-label" htmlFor={`o-${key}`}>
          {label}{!hasValue && <span className="orientation-required"> *</span>}
        </label>
        {description && <p className="orientation-help">{description}</p>}
        <textarea
          id={`o-${key}`}
          className="textarea kb-textarea"
          rows={3}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || ""}
          data-testid={`orientation-input-${key}`}
        />
      </div>
    );
  }

  // Default — short_answer (single-line text input).
  return (
    <div className="orientation-field" data-testid={`orientation-field-${key}`}>
      <label className="orientation-label" htmlFor={`o-${key}`}>
        {label}{!hasValue && <span className="orientation-required"> *</span>}
      </label>
      {description && <p className="orientation-help">{description}</p>}
      <input
        id={`o-${key}`}
        type="text"
        className="textarea kb-textarea"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        data-testid={`orientation-input-${key}`}
      />
    </div>
  );
}
